import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  useFetchEcommerceInventoryMutation,
  useUpdateEcommerceInventoryMutation,
  useFetchEcommerceInventoryHistoryMutation,
} from "../../../features/tenant/ecommerceProduct/ecommerceInventoryApi";
import {
  FiPackage,
  FiPlus,
  FiMinus,
  FiAlertCircle,
  FiClock,
  FiRefreshCw,
  FiSearch,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiChevronUp,
  FiSliders,
  FiUser
} from "react-icons/fi";

// ─── Helpers & Config ──────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  add: {
    label: "Stock Added",
    color: "text-emerald-700 bg-emerald-50 border-emerald-100",
    dot: "bg-emerald-500",
    icon: <FiPlus size={12} />
  },
  subtract: {
    label: "Stock Removed",
    color: "text-amber-700 bg-amber-50 border-amber-100",
    dot: "bg-amber-500",
    icon: <FiMinus size={12} />
  },
  set: {
    label: "Stock Overridden",
    color: "text-indigo-700 bg-indigo-50 border-indigo-100",
    dot: "bg-indigo-500",
    icon: <FiRefreshCw size={12} />
  },
  out_of_stock: {
    label: "Marked Out Of Stock",
    color: "text-rose-700 bg-rose-50 border-rose-100",
    dot: "bg-rose-500",
    icon: <FiAlertCircle size={12} />
  },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

// Helper to get inventory for any size (group or individual)
const getDisplayInventory = (variant) => {
  return variant.current_inventory ?? 0;
};

// ─── Adjust Stock Modal ────────────────────────────────────────────────────────

function AdjustModal({ variant, onClose, onSubmit, isLoading }) {
  const [type, setType] = useState("add");
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");

  const typeOptions = [
    { value: "add", label: "➕ Add Stock", desc: "Increase current quantity" },
    { value: "subtract", label: "➖ Remove Stock", desc: "Decrease current quantity" },
    // { value: "set", label: "✏️ Set Exact", desc: "Override stock count" },
    { value: "out_of_stock", label: "🚫 Out of Stock", desc: "Force count to 0" },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (type !== "out_of_stock" && (!quantity || parseInt(quantity) < 0)) {
      toast.error("Please enter a valid quantity");
      return;
    }
    onSubmit({
      product_size_id: variant.product_size_id,
      type,
      quantity: type === "out_of_stock" ? 0 : parseInt(quantity),
      note
    });
  };

  const currentStock = variant.current_inventory ?? 0;
  const parsedQty = parseInt(quantity) || 0;
  let newStock = currentStock;

  if (type === "add") newStock = currentStock + parsedQty;
  else if (type === "subtract") newStock = Math.max(0, currentStock - parsedQty);
  else if (type === "set") newStock = parsedQty;
  else if (type === "out_of_stock") newStock = 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden transform transition-all border border-slate-100">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-base font-bold text-slate-800">Adjust Inventory</h3>
            <div className="flex items-center gap-2 mt-1">
              <span
                className="w-3.5 h-3.5 rounded-full border border-slate-200 shadow-sm flex-shrink-0"
                style={{ backgroundColor: variant.hex_code }}
              />
              <span className="text-xs font-medium text-slate-500">
                {variant.product_name} • {variant.color_name} / {variant.size_name}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors">
            <FiX size={18} />
          </button>
        </div>

        {/* Current status */}
        <div className="flex items-center justify-between px-6 py-3.5 bg-slate-50 border-b border-slate-100">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Current Stock</span>
          <span className={`text-sm font-bold ${currentStock <= 0 ? "text-rose-600" : currentStock <= 10 ? "text-amber-600" : "text-emerald-600"}`}>
            {currentStock} units
          </span>
        </div>

        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
          {/* Action type */}
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Select Action</label>
            <div className="grid grid-cols-2 gap-2">
              {typeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setType(opt.value)}
                  className={`flex flex-col items-start px-3 py-2.5 rounded-xl border text-left transition-all ${
                    type === opt.value
                      ? "border-indigo-600 bg-indigo-50/50 ring-2 ring-indigo-100 text-indigo-950 font-medium"
                      : "border-slate-200 hover:border-slate-300 bg-white text-slate-700"
                  }`}
                >
                  <span className="text-xs font-bold">{opt.label}</span>
                  <span className="text-[10px] text-slate-400 mt-0.5 leading-tight">{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quantity */}
          {type !== "out_of_stock" && (
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wider">
                {type === "set" ? "New Quantity (units)" : "Quantity to Change (units)"}
              </label>
              <input
                type="number"
                value={quantity}
                min="0"
                required
                onChange={(e) => setQuantity(e.target.value)}
                placeholder={type === "set" ? "e.g. 150" : "e.g. 20"}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}

          {/* Note */}
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wider">
              Note / Reason <span className="text-slate-400 font-normal normal-case">(optional)</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Received new stock from supplier, Damaged products removed..."
              rows={2}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>

          {/* Preview result */}
          {quantity && type !== "out_of_stock" && (
            <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 text-sm">
              <span className="text-slate-500">Preview new stock level:</span>
              <span className="font-bold text-slate-800">{newStock} units</span>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
            >
              {isLoading ? "Updating..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

const EcommerceInventory = () => {
  const navigate = useNavigate();
  const vendorData = JSON.parse(sessionStorage.getItem("vendorData")) || {};
  const tenantName = vendorData.tenant_name;
  const tenant_id = vendorData.tenant_id;
  const token = sessionStorage.getItem("access_token");

  const [activeTab, setActiveTab] = useState("inventory"); // "inventory" | "history"
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "instock" | "lowstock" | "outofstock"
  
  // Expanded products state (accordion keys)
  const [expandedProducts, setExpandedProducts] = useState({});

  const [adjustingVariant, setAdjustingVariant] = useState(null);
  const [inventoryData, setInventoryData] = useState([]);
  
  // History tab states
  const [historyData, setHistoryData] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyProductFilter, setHistoryProductFilter] = useState("all");
  const HISTORY_LIMIT = 20;

  const [fetchInventory, { isLoading: isFetchingInventory }] = useFetchEcommerceInventoryMutation();
  const [updateInventory, { isLoading: isUpdating }] = useUpdateEcommerceInventoryMutation();
  const [fetchHistory, { isLoading: isFetchingHistory }] = useFetchEcommerceInventoryHistoryMutation();

  const loadInventory = () => {
    fetchInventory({ slug: tenant_id, token })
      .unwrap()
      .then((res) => {
        if (res?.body) {
          setInventoryData(res.body);
          // Expand first product by default if we have data
          if (res.body.length > 0) {
            const firstProdId = res.body[0].product_id;
            setExpandedProducts(prev => ({ ...prev, [firstProdId]: true }));
          }
        }
      })
      .catch(() => toast.error("Failed to load inventory"));
  };

  const loadHistory = (page = 1, productId = "all") => {
    const body = { page, limit: HISTORY_LIMIT };
    if (productId !== "all") {
      body.product_id = parseInt(productId);
    }
    fetchHistory({ slug: tenant_id, token, ...body })
      .unwrap()
      .then((res) => {
        if (res?.body) {
          setHistoryData(res.body.data || []);
          setHistoryTotal(res.body.total || 0);
          setHistoryPage(page);
        }
      })
      .catch(() => toast.error("Failed to load inventory history"));
  };

  useEffect(() => {
    loadInventory();
  }, []);

  useEffect(() => {
    if (activeTab === "history") {
      loadHistory(1, historyProductFilter);
    }
  }, [activeTab, historyProductFilter]);

  const handleAdjust = (data) => {
    updateInventory({ slug: tenant_id, token, ...data })
      .unwrap()
      .then(() => {
        toast.success("Inventory level updated successfully");
        setAdjustingVariant(null);
        loadInventory();
        if (activeTab === "history") loadHistory(1, historyProductFilter);
      })
      .catch((err) => toast.error(err?.data?.message || "Failed to update inventory"));
  };

  const toggleProductExpand = (prodId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [prodId]: !prev[prodId]
    }));
  };

  // Group variants by product
  const getGroupedProducts = () => {
    const grouped = {};
    inventoryData.forEach((item) => {
      const pId = item.product_id;
      if (!grouped[pId]) {
        grouped[pId] = {
          product_id: pId,
          product_name: item.product_name,
          article_no: item.article_no,
          variants: []
        };
      }
      grouped[pId].variants.push(item);
    });

    // Apply search and status filters
    const filteredProducts = [];
    const query = search.toLowerCase();

    Object.values(grouped).forEach((prod) => {
      const matchesSearch =
        prod.product_name?.toLowerCase().includes(query) ||
        prod.article_no?.toLowerCase().includes(query) ||
        prod.variants.some(v =>
          v.color_name?.toLowerCase().includes(query) ||
          v.size_name?.toLowerCase().includes(query)
        );

      if (!matchesSearch) return;

      // Filter variants within the product
      const filteredVariants = prod.variants.filter((v) => {
        const inv = getDisplayInventory(v);
        const active = v.status === "1";
        
        if (statusFilter === "outofstock") {
          return !active || inv <= 0;
        }
        if (statusFilter === "lowstock") {
          return active && inv > 0 && inv <= 10;
        }
        if (statusFilter === "instock") {
          return active && inv > 10;
        }
        return true;
      });

      if (filteredVariants.length > 0 || statusFilter === "all") {
        filteredProducts.push({
          ...prod,
          variants: filteredVariants
        });
      }
    });

    return filteredProducts;
  };

  const grouped = getGroupedProducts();
  const totalPages = Math.ceil(historyTotal / HISTORY_LIMIT);

  // Get stock badge details
  const getStockBadge = (inv, status) => {
    if (status === "0" || inv <= 0) {
      return { text: "Out of Stock", cls: "bg-rose-50 text-rose-700 border-rose-100" };
    }
    if (inv <= 10) {
      return { text: `Low Stock (${inv})`, cls: "bg-amber-50 text-amber-700 border-amber-100" };
    }
    return { text: `In Stock (${inv})`, cls: "bg-emerald-50 text-emerald-700 border-emerald-100" };
  };

  // Get distinct list of products for the dropdown filter in History tab
  const getUniqueProducts = () => {
    const list = {};
    inventoryData.forEach((item) => {
      list[item.product_id] = {
        name: item.product_name,
        code: item.article_no || item.product_code
      };
    });
    return Object.entries(list).map(([id, info]) => ({ id, name: info.name, code: info.code }));
  };

  return (
    <div className="p-5 bg-slate-50/50 min-h-screen">
      {/* Header card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2.5">
              <FiPackage className="text-indigo-600" size={24} /> Inventory Management
            </h1>
            <p className="text-xs text-slate-400 mt-1">
              View and audit current stock levels. Adjust initial quantities, log overrides, and review changes product-wise.
            </p>
          </div>
          <button
            onClick={() => navigate(`/genefied/${tenantName}/ecommerce-products`)}
            className="text-xs font-semibold text-slate-600 hover:text-indigo-600 bg-slate-100 hover:bg-slate-200/60 px-4 py-2.5 rounded-xl transition-all"
          >
            ← Back to Products
          </button>
        </div>
      </div>

      {/* Tabs Layout */}
      <div className="flex gap-1.5 p-1 bg-slate-200/50 rounded-xl w-fit mb-6">
        <button
          onClick={() => setActiveTab("inventory")}
          className={`px-5 py-2.5 rounded-lg text-xs font-bold transition-all ${
            activeTab === "inventory"
              ? "bg-white text-indigo-700 shadow-sm"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          📦 Product Inventory Matrix
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className={`px-5 py-2.5 rounded-lg text-xs font-bold transition-all ${
            activeTab === "history"
              ? "bg-white text-indigo-700 shadow-sm"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          🕐 Product-Wise History Log
        </button>
      </div>

      {/* ── Inventory Tab ─────────────────────────────────────────────────── */}
      {activeTab === "inventory" && (
        <div className="flex flex-col gap-5">
          {/* Toolbar */}
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-wrap gap-3 items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-[280px]">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search products by name, code, article..."
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Status Dropdown */}
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none bg-slate-50 border border-slate-200 text-slate-600 rounded-xl pl-3.5 pr-8 py-2 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">🟢 All Stock States</option>
                  <option value="instock">👍 In Stock (&gt;10)</option>
                  <option value="lowstock">⚠️ Low Stock (1-10)</option>
                  <option value="outofstock">🚫 Out of Stock (0)</option>
                </select>
                <FiSliders className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={12} />
              </div>
            </div>

            <button
              onClick={loadInventory}
              disabled={isFetchingInventory}
              className="flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-indigo-600 border border-slate-200 hover:border-slate-300 bg-white px-4 py-2.5 rounded-xl transition-all disabled:opacity-55"
            >
              <FiRefreshCw size={12} className={isFetchingInventory ? "animate-spin" : ""} />
              Refresh Inventory
            </button>
          </div>

          {/* Product Wise Accordion Table List */}
          {isFetchingInventory ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-16 text-center text-slate-400">
              <FiRefreshCw className="animate-spin mx-auto mb-3 text-indigo-400" size={32} />
              <p className="text-xs font-medium">Fetching product inventory mappings...</p>
            </div>
          ) : grouped.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-16 text-center text-slate-400">
              <FiPackage className="mx-auto mb-4 text-slate-300" size={42} />
              <p className="text-sm font-semibold text-slate-600">No Product Inventory Found</p>
              <p className="text-xs text-slate-400 mt-1">
                {search || statusFilter !== "all" ? "Try adjusting your search queries or status filters." : "Create products and map their sizes/colors to manage inventory."}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {grouped.map((prod) => {
                const totalStock = prod.variants.reduce((sum, v) => sum + (v.current_inventory ?? 0), 0);
                const isExpanded = !!expandedProducts[prod.product_id];
                
                return (
                  <div
                    key={prod.product_id}
                    className={`bg-white rounded-2xl border shadow-sm transition-all overflow-hidden ${
                      isExpanded ? "border-slate-200 ring-1 ring-slate-100" : "border-slate-100 hover:border-slate-200"
                    }`}
                  >
                    {/* Product Card Header */}
                    <div
                      onClick={() => toggleProductExpand(prod.product_id)}
                      className="p-5 flex items-center justify-between gap-4 cursor-pointer hover:bg-slate-50/50 transition-colors select-none"
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        {/* Package Visual Icon */}
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100/60 flex items-center justify-center text-indigo-600 flex-shrink-0">
                          <FiPackage size={18} />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-slate-800 text-sm truncate">{prod.product_name}</h3>
                          <p className="text-[11px] text-slate-400 font-medium mt-0.5">Article No: {prod.article_no || "—"}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 flex-shrink-0">
                        {/* Summary Badges */}
                        <div className="text-right hidden sm:block">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Available Stock</span>
                          <span className={`text-sm font-black ${totalStock === 0 ? "text-rose-600" : totalStock <= 20 ? "text-amber-600" : "text-emerald-600"}`}>
                            {totalStock} units
                          </span>
                        </div>
                        <div className="h-8 w-px bg-slate-100 hidden sm:block" />
                        <div className="text-right">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Variants Mapping</span>
                          <span className="text-xs font-bold text-slate-700">{prod.variants.length} combinations</span>
                        </div>

                        {/* Chevron Expand Indicator */}
                        <div className="p-1 rounded-lg bg-slate-50 text-slate-500 border border-slate-200/60">
                          {isExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                        </div>
                      </div>
                    </div>

                    {/* Product Variant Grid Matrix */}
                    {isExpanded && (
                      <div className="border-t border-slate-100 bg-slate-50/30">
                        <div className="overflow-x-auto">
                          <table className="w-full text-left border-collapse">
                            <thead>
                              <tr className="bg-slate-50/80 border-b border-slate-100 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                <th className="px-6 py-3">Variant (Color & Color Code)</th>
                                <th className="px-6 py-3">Size Mapping</th>
                                <th className="px-6 py-3 text-center">MRP Value</th>
                                <th className="px-6 py-3 text-center">Available Stock</th>
                                <th className="px-6 py-3 text-center">Stock Badge</th>
                                <th className="px-6 py-3 text-right">Adjustment Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 bg-white">
                              {prod.variants.map((variant) => {
                                const displayStock = getDisplayInventory(variant);
                                const badge = getStockBadge(displayStock, variant.status);
                                return (
                                  <tr key={variant.product_size_id} className="hover:bg-slate-50/30 transition-colors">
                                    {/* Color info */}
                                    <td className="px-6 py-3.5 whitespace-nowrap">
                                      <div className="flex items-center gap-2.5">
                                        <span
                                          className="w-5.5 h-5.5 rounded-full border border-slate-200 shadow-sm flex-shrink-0"
                                          style={{ backgroundColor: variant.hex_code }}
                                        />
                                        <div>
                                          <span className="text-xs font-semibold text-slate-800">{variant.color_name}</span>
                                          <span className="text-[10px] text-slate-400 block font-mono">{variant.hex_code}</span>
                                        </div>
                                      </div>
                                    </td>

                                    {/* Size */}
                                    <td className="px-6 py-3.5 whitespace-nowrap">
                                      <span className="inline-flex items-center justify-center bg-slate-100 border border-slate-200 text-slate-700 text-xs font-bold px-2.5 py-0.5 rounded-lg">
                                        {variant.size_name}
                                      </span>
                                    </td>

                                    {/* MRP */}
                                    <td className="px-6 py-3.5 text-center whitespace-nowrap text-xs font-bold text-slate-700">
                                      ₹{variant.mrp}
                                    </td>

                                    {/* Current inventory level */}
                                    <td className="px-6 py-3.5 text-center whitespace-nowrap text-xs font-black text-slate-800">
                                      {displayStock} units
                                    </td>

                                    {/* Stock Badge */}
                                    <td className="px-6 py-3.5 text-center whitespace-nowrap">
                                      <span className={`inline-flex text-[10px] font-bold px-2.5 py-1 rounded-full border ${badge.cls}`}>
                                        {badge.text}
                                      </span>
                                    </td>

                                    {/* Actions */}
                                    <td className="px-6 py-3.5 text-right whitespace-nowrap">
                                      <button
                                        onClick={() => setAdjustingVariant(variant)}
                                        className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-white bg-indigo-50 hover:bg-indigo-600 border border-indigo-200 hover:border-indigo-600 px-3.5 py-1.5 rounded-lg transition-all"
                                      >
                                        <FiSliders size={12} /> Adjust Stock
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── History Tab (Product Wise Change Timeline Log) ──────────────────── */}
      {activeTab === "history" && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          {/* History header & Filter */}
          <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex items-center gap-3.5 flex-1 min-w-[280px]">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Product Filter:</span>
              <div className="relative flex-1 max-w-sm">
                <select
                  value={historyProductFilter}
                  onChange={(e) => setHistoryProductFilter(e.target.value)}
                  className="appearance-none w-full bg-white border border-slate-200 text-slate-700 rounded-xl pl-4 pr-10 py-2 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">📂 View Log of All Products</option>
                  {getUniqueProducts().map((p) => (
                    <option key={p.id} value={p.id}>
                      📦 {p.name} {p.code ? `(${p.code})` : ""}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
              </div>
            </div>

            <button
              onClick={() => loadHistory(historyPage, historyProductFilter)}
              disabled={isFetchingHistory}
              className="flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-indigo-600 border border-slate-200 bg-white px-4 py-2.5 rounded-xl transition-all"
            >
              <FiRefreshCw size={12} className={isFetchingHistory ? "animate-spin" : ""} />
              Refresh Logs
            </button>
          </div>

          {/* Timeline list logs */}
          {isFetchingHistory ? (
            <div className="p-16 text-center text-slate-400">
              <FiRefreshCw className="animate-spin mx-auto mb-3 text-indigo-400" size={32} />
              <p className="text-xs font-medium">Fetching change timeline records...</p>
            </div>
          ) : historyData.length === 0 ? (
            <div className="p-16 text-center text-slate-400">
              <FiClock className="mx-auto mb-3 text-slate-300" size={42} />
              <p className="text-sm font-semibold text-slate-600">No Inventory Audit Logs</p>
              <p className="text-xs text-slate-400 mt-1">
                {historyProductFilter !== "all" ? "No audit records found for this specific product." : "Inventory changes will be logged here automatically."}
              </p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-100">
                {historyData.map((entry) => {
                  const cfg = TYPE_CONFIG[entry.type] || TYPE_CONFIG.set;
                  return (
                    <div key={entry.id} className="p-5 flex items-start gap-4 hover:bg-slate-50/40 transition-colors">
                      {/* Status Icon Indicator */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.color} border`}>
                        {cfg.icon}
                      </div>

                      {/* Log description details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                          <span className="font-bold text-slate-800 text-sm truncate">{entry.product_name}</span>
                          <span className="text-slate-300 text-xs hidden sm:inline">•</span>
                          
                          {/* Variant sub details */}
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span
                              className="w-3.5 h-3.5 rounded-full border border-slate-200 shadow-sm flex-shrink-0"
                              style={{ backgroundColor: entry.hex_code }}
                            />
                            <span className="text-[11px] font-medium text-slate-600">{entry.color_name}</span>
                            <span className="inline-flex bg-slate-100 text-slate-700 text-[10px] font-bold px-1.5 py-0.2 rounded">
                              {entry.size_name}
                            </span>
                          </div>
                        </div>

                        {/* Audit Details */}
                        <div className="flex flex-wrap items-center gap-3 mt-1.5">
                          <span className={`inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.color}`}>
                            {cfg.label}
                          </span>
                          
                          <span className="text-xs text-slate-500 font-medium">
                            Stock level changed from <span className="font-bold text-slate-700">{entry.previous_inventory}</span> → <span className="font-black text-slate-900">{entry.new_inventory}</span> units
                            {entry.change_quantity != null && (
                              <span className={`ml-1.5 font-bold ${entry.change_quantity >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                                ({entry.change_quantity >= 0 ? "+" : ""}{entry.change_quantity})
                              </span>
                            )}
                          </span>
                        </div>

                        {/* Note comments */}
                        {entry.note && (
                          <div className="mt-2 text-xs text-slate-500 italic bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100 max-w-xl">
                            "{entry.note}"
                          </div>
                        )}
                      </div>

                      {/* Right date & modifier */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-[11px] font-bold text-slate-500">{fmtDate(entry.created_at)}</p>
                        {entry.created_by_name && (
                          <p className="text-[10px] text-slate-400 mt-1 flex items-center justify-end gap-1.5 font-semibold">
                            <FiUser className="text-slate-400" size={10} /> {entry.created_by_name}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-4 border-t border-slate-100 bg-slate-50/50">
                  <p className="text-xs text-slate-500 font-semibold">
                    Showing page {historyPage} of {totalPages} — {historyTotal} logged changes
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => loadHistory(historyPage - 1, historyProductFilter)}
                      disabled={historyPage <= 1 || isFetchingHistory}
                      className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 border border-slate-200 bg-white px-3 py-2 rounded-xl hover:bg-slate-50 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      <FiChevronLeft size={14} /> Prev
                    </button>
                    <button
                      onClick={() => loadHistory(historyPage + 1, historyProductFilter)}
                      disabled={historyPage >= totalPages || isFetchingHistory}
                      className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 border border-slate-200 bg-white px-3 py-2 rounded-xl hover:bg-slate-50 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      Next <FiChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Adjust Stock Modal overlay component */}
      {adjustingVariant && (
        <AdjustModal
          variant={adjustingVariant}
          onClose={() => setAdjustingVariant(null)}
          onSubmit={handleAdjust}
          isLoading={isUpdating}
        />
      )}
    </div>
  );
};

export default EcommerceInventory;
